import { useState } from 'react';
import {
  CopyPlusIcon,
  DownloadIcon,
  FileTextIcon,
  LibraryBigIcon,
  LinkIcon,
  Redo2Icon,
  Share2Icon,
  Undo2Icon,
  WandSparklesIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCanWork } from '@/features/workspaces/canWork';
import { Link, useNavigate } from '@tanstack/react-router';
import { SquareKanbanIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useDraft } from '../builder/useDraftContext';
import { tidyLayout } from '../builder/draftOps';
import { OUTDATED_DRAFT_MESSAGE, draftStatusLine } from '../builder/draftCopy';
import {
  isPendingChangesOutdated,
  isStepsInUse,
  isVersionConflict,
  isWorkflowGone,
  isWorkflowRejection,
} from '../errors';
import type { DraftWrites } from '../hooks/useDraftWrites';
import type { WorkflowDraft } from '../builder/types';
import { ContractPreviewDialog } from './ContractPreviewDialog';
import { DiscardDraftDialog } from './DiscardDraftDialog';
import { useCopyWorkflowLink } from '../hooks/useCopyWorkflowLink';
import { CopyToWorkspaceDialog } from './CopyToWorkspaceDialog';
import { ExportWorkflowDialog } from './ExportWorkflowDialog';
import { ShareToLibraryDialog } from './ShareToLibraryDialog';
import { PublishChangesDialog } from './PublishChangesDialog';
import { PublishDialog } from './PublishDialog';

/**
 * The builder's header: what this workflow is, and the actions that commit it.
 *
 * Save keeps a draft a draft, and is deliberately never gated on validity — the
 * whole point of the draft tier is that half-finished work is storable, and a
 * Save that refused it would put the user back where they started, unable to
 * stop mid-thought.
 *
 * Publish is the opposite, and appears only while the workflow is a draft
 * (FR-007). Once published there is nothing for it to do: publication is
 * one-way, and an enabled control that can only fail is worse than no control.
 *
 * Once published, Save keeps the edits as the workflow's draft beside the live
 * version (spec 144 FR-003), and **Publish changes** is what puts that draft
 * live — offered only while there is one, saved or on screen. **Discard
 * draft** throws a saved one away.
 */
export function BuilderHeader({
  workspaceId,
  workflowId,
  workflowKey,
  writes,
  onReload,
}: {
  workspaceId: string;
  workflowId: string;
  /** For the way out to this workflow's board (spec 128). */
  workflowKey: string;
  /** Save and Publish, shared with the leave dialog (spec 144). */
  writes: DraftWrites;
  /** Re-read the workflow, discarding the local draft. */
  onReload: () => void;
}) {
  const { draft, edit, undo, redo, canUndo, canRedo } = useDraft();
  const { failure: error, isPending } = writes;
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishChangesOpen, setPublishChangesOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const copyLink = useCopyWorkflowLink(workspaceId, workflowKey);
  const canWork = useCanWork();

  // Nothing to arrange in an empty workflow, and a control whose only outcome
  // is "nothing happened" is one a user has to click to learn that.
  const hasSteps = draft.phases.some((phase) => phase.steps.length > 0);

  const publish = () => {
    if (isPending) return;
    void writes
      .publish()
      .then((published) => {
        if (!published) return;
        // Publishing is the moment the workflow becomes useful; the person is
        // offered its board at once rather than left on the canvas (spec 128
        // FR-008).
        toast.success(`Published ${draft.name}`, {
          action: {
            label: 'Open board',
            onClick: () => {
              void navigate({
                to: '/workspaces/$workspaceId/workflows/$workflowKey/board',
                params: { workspaceId, workflowKey },
              });
            },
          },
        });
      })
      // Closed whether it succeeded or not. A refusal lists its problems in
      // the inspector, and leaving the modal up would put that list behind
      // something the user has to dismiss first — with the canvas they need to
      // fix inert behind it.
      .finally(() => setPublishOpen(false));
  };

  const publishChanges = () => {
    if (isPending) return;
    void writes
      .publishChanges()
      // Closed either way, for the reason Publish closes: a refusal is listed
      // in the inspector, and the modal would stand between it and the canvas.
      .finally(() => setPublishChangesOpen(false));
  };

  const discardDraft = () => {
    if (isPending) return;
    void writes.discardDraft().finally(() => setDiscardOpen(false));
  };

  const published = draft.status === 'published';
  // A draft exists to publish once anything differs from the live version:
  // a saved one, or edits not yet saved — which Publish changes saves first.
  const hasDraft = published && (draft.pending !== null || draft.dirty);
  // Said as soon as the draft is opened (FR-011), not left for a publish to
  // be refused over. Once a refusal says it, the refusal says it alone.
  const showOutdated =
    draft.pending?.outdated === true &&
    (error === null || !isPendingChangesOutdated(error));

  return (
    <header className="flex flex-wrap items-center gap-3">
      <div className="flex min-w-0 items-baseline gap-2.5">
        {/* The workflow row this page was opened from carries into this
            heading (spec 075 FR-026). Keyed on the workflow's id — the key is
            what the *address* uses, but the id is the entity's own identifier
            and cannot be re-pointed (FR-028). */}
        <h1
          style={{ viewTransitionName: `workflow-${workflowId}` }}
          className="text-foreground truncate text-xl font-medium tracking-tight"
        >
          {draft.name}
        </h1>
        {draft.status === 'draft' && <Badge variant="outline">Draft</Badge>}
      </div>

      {/* The way out to what this workflow drives (spec 128 FR-009). A link,
          so the builder's unsaved-changes guard sees the navigation. */}
      {draft.status === 'published' ? (
        <Link
          to="/workspaces/$workspaceId/workflows/$workflowKey/board"
          params={{ workspaceId, workflowKey }}
          className={buttonVariants({ variant: 'ghost', size: 'sm' })}
        >
          <SquareKanbanIcon aria-hidden="true" strokeWidth={1.5} />
          View board
        </Link>
      ) : null}

      <div className="ml-auto flex items-center gap-3">
        {/*
          Visible as well as bound to ⌘Z: a keyboard shortcut nobody can see is
          a feature only the people who wrote it know about, and the disabled
          state is what tells a user there is nothing left to undo.
        */}
        <span className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Undo"
            disabled={!canUndo}
            onClick={undo}
          >
            <Undo2Icon />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Redo"
            disabled={!canRedo}
            onClick={redo}
          >
            <Redo2Icon />
          </Button>
          {/*
            Grouped with undo and redo rather than with Save and Publish,
            because it belongs to the same family: it acts on the whole
            document rather than on whatever is selected, and it changes
            nothing anyone else can see until the draft is saved.

            Sitting next to Undo is also the honest place for it. One click
            discards every position the user placed by hand, and the control
            that puts them back is the one immediately to its left.
          */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => edit(tidyLayout())}
            disabled={!hasSteps}
            title="Re-arrange the canvas from the shape of the workflow"
          >
            <WandSparklesIcon />
            Tidy up
          </Button>
        </span>
        {/*
          The dirty indicator, in words rather than a dot: "unsaved changes" is
          unambiguous where a coloured dot has to be learned (FR-045).
        */}
        <p
          className="text-muted-foreground text-xs"
          data-testid="builder-save-state"
        >
          {saveStateText(draft, isPending)}
        </p>
        {/*
          "Preview definition" rather than "Definition": a phase is very often
          called Definition, and the phase strip sits directly above this. Two
          controls with the same word, one an action and one a place, is a
          collision a user has to resolve by clicking.
        */}
        <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(true)}>
          <FileTextIcon />
          Preview definition
        </Button>
        {/* Only what is published travels (spec 203), so a never-published draft
            has nothing to export yet. */}
        {/* Export, Copy to workspace, Copy link and Share to Library sit behind
            one Share menu (spec 210, the review's P8): as separate buttons
            beside Tidy up and Preview they crowded the header and truncated. */}
        {draft.status === 'published' && (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={<Button variant="outline" size="sm" />}
            >
              <Share2Icon />
              Share
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-48">
              <DropdownMenuItem onClick={() => setExportOpen(true)}>
                <DownloadIcon aria-hidden="true" />
                Export…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setCopyOpen(true)}>
                <CopyPlusIcon aria-hidden="true" />
                Copy to workspace…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => void copyLink()}>
                <LinkIcon aria-hidden="true" />
                Copy link
              </DropdownMenuItem>
              {canWork ? (
                <DropdownMenuItem onClick={() => setShareOpen(true)}>
                  <LibraryBigIcon aria-hidden="true" />
                  Share to Library…
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {/* The builder's own action, so it carries the page's one filled
            primary — it used to be an outline button sitting beside a filled
            one, which read as the less important of the two. Publish steps
            back to outline: it opens a confirmation whose own confirm button
            is that dialog's primary, so the commitment is still stated in
            full, one surface later. */}
        <Button
          size="sm"
          onClick={() => void writes.save()}
          disabled={isPending || !draft.dirty}
        >
          Save
        </Button>
        {draft.status === 'draft' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPublishOpen(true)}
            disabled={isPending}
          >
            Publish
          </Button>
        )}
        {hasDraft && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPublishChangesOpen(true)}
            disabled={isPending}
          >
            Publish changes
          </Button>
        )}
        {/* Only a saved draft: unsaved edits alone are undone or left behind,
            and there would be nothing on the server to throw away. */}
        {published && draft.pending !== null && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDiscardOpen(true)}
            disabled={isPending}
          >
            Discard draft
          </Button>
        )}
      </div>

      {showOutdated && (
        <div
          role="status"
          aria-label="Out-of-date draft"
          className="flex w-full flex-wrap items-center gap-3 text-sm"
        >
          <p className="text-foreground">{OUTDATED_DRAFT_MESSAGE}</p>
          {/* The same two ways on as after a refused publish: throw the draft
              away, or reload and carry the changes over by hand. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDiscardOpen(true)}
          >
            Discard draft
          </Button>
          <Button variant="outline" size="sm" onClick={onReload}>
            Reload
          </Button>
        </div>
      )}

      {error !== null && (
        <div
          role="alert"
          className="flex w-full flex-wrap items-center gap-3 text-sm"
        >
          <p className="text-destructive">{saveErrorMessage(error)}</p>
          {/*
            A conflict is the one refusal the user cannot fix by editing: the
            document is not wrong, it is merely stale. Offering Reload here is
            the difference between telling someone what happened and giving them
            the way out (FR-048).

            Deliberately not automatic. Reloading discards their unsaved edits,
            and the service wrote nothing — so the work is still on screen and
            still theirs to copy out first.
          */}
          {isVersionConflict(error) && (
            <Button variant="outline" size="sm" onClick={onReload}>
              Reload this workflow
            </Button>
          )}
          {/*
            An out-of-date draft cannot be fixed by editing either, and
            reloading alone brings back the same stale draft (FR-011). The two
            ways on are to throw it away, or to reload and carry the changes
            over by hand.
          */}
          {isPendingChangesOutdated(error) && (
            <>
              {draft.pending !== null && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDiscardOpen(true)}
                >
                  Discard draft
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={onReload}>
                Reload
              </Button>
            </>
          )}
        </div>
      )}

      <ContractPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} />
      <ExportWorkflowDialog
        workspaceId={workspaceId}
        workflow={{ id: workflowId, key: workflowKey, name: draft.name }}
        open={exportOpen}
        onOpenChange={setExportOpen}
      />
      <ShareToLibraryDialog
        workspaceId={workspaceId}
        workflow={{ id: workflowId, key: workflowKey, name: draft.name }}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
      <CopyToWorkspaceDialog
        workspaceId={workspaceId}
        workflow={{ id: workflowId, key: workflowKey, name: draft.name }}
        open={copyOpen}
        onOpenChange={setCopyOpen}
      />

      <PublishDialog
        workflowName={draft.name}
        open={publishOpen}
        isPending={isPending}
        onOpenChange={setPublishOpen}
        onConfirm={publish}
      />

      <PublishChangesDialog
        workflowName={draft.name}
        open={publishChangesOpen}
        isPending={isPending}
        onOpenChange={setPublishChangesOpen}
        onConfirm={publishChanges}
      />

      {draft.pending !== null && (
        <DiscardDraftDialog
          savedBy={draft.pending.savedBy}
          savedAt={draft.pending.savedAt}
          open={discardOpen}
          isPending={isPending}
          onOpenChange={setDiscardOpen}
          onConfirm={discardDraft}
        />
      )}
    </header>
  );
}

/**
 * The dirty indicator's words.
 *
 * A workflow never published says what it always has. A published one says
 * whether what is on screen is live (spec 144 FR-005): a saved draft is not,
 * and saying who saved it and when is what tells a colleague's work in
 * progress from your own.
 */
function saveStateText(draft: WorkflowDraft, isPending: boolean): string {
  if (isPending) return 'Saving…';
  if (draft.dirty) return 'Unsaved changes';
  if (draft.status !== 'published') return 'All changes saved';
  return draft.pending
    ? draftStatusLine(
        draft.pending.savedBy,
        draft.pending.savedAt,
        draft.pending.savedVia,
      )
    : 'All changes published';
}

/**
 * What to tell the user, per refusal.
 *
 * Four refusals, four different things to do about them. Collapsing them into
 * "could not save" would leave someone whose only problem is a stale copy
 * hunting for a mistake in their document.
 */
function saveErrorMessage(error: Error): string {
  if (isVersionConflict(error)) return error.message;
  if (isPendingChangesOutdated(error)) return error.message;
  if (isWorkflowGone(error)) return error.message;

  if (isStepsInUse(error)) {
    // Named steps and named tasks; the list below repeats them selectably, and
    // this line says what kind of refusal it was.
    return 'Some steps you removed still have tasks on them.';
  }

  if (isWorkflowRejection(error)) {
    return error.violations.length === 1
      ? error.violations[0].message
      : `${String(error.violations.length)} problems must be fixed first.`;
  }

  return error.message;
}
